// @ts-nocheck
import { getSpeechRecognitionCtor } from "./speechRecognition";
import {
  CUSTOMER_NATIVE_PREFER_ON_DEVICE,
  CUSTOMER_NATIVE_ON_DEVICE_QUALITY,
  CUSTOMER_NATIVE_SILENCE_RMS,
  CUSTOMER_NATIVE_LEAK_STRIKES,
  CUSTOMER_NATIVE_LEAK_WINDOW_MS,
} from "@/utilities/constant";

const defaultLog = (label, detail) => {
  if (detail !== undefined) {
    // eslint-disable-next-line no-console
    // console.log(`[CUSTOMER_TRANSCRIPTION] ${label}`, detail);
  } else {
    // eslint-disable-next-line no-console
    // console.log(`[CUSTOMER_TRANSCRIPTION] ${label}`);
  }
};

function defaultGetStream() {
  const el = document.getElementById("remoteAudio");
  return (el && el.srcObject) || null;
}

export function createCustomerNativeTranscriptionService({
  getCtor = getSpeechRecognitionCtor,
  lang = "hi-IN",
  getStream = defaultGetStream,
  isAudioTrackSupported = () => true,
  onAudioTrackUnsupported = () => {},
  preferOnDevice = CUSTOMER_NATIVE_PREFER_ON_DEVICE,
  onDeviceQuality = CUSTOMER_NATIVE_ON_DEVICE_QUALITY,
  shouldRestart = () => false,
  onInterim = () => {},
  onFinal = () => {},
  onError = () => {},
  onStatus = () => {},
  log = defaultLog,
  cloneTrack = false,
  useAudioBridge = true,
  networkFailureLimit = 3,
  restartDelayMs = 300,
  maxRestartDelayMs = 3000,
  attachRetryMs = 500,
  silenceTimeoutMs = 30000,
  noResultTimeoutMs = 45000,
} = {}) {
  let recognition = null;
  let clonedTrack = null;
  let running = false;
  let manualStop = false;
  let paused = false;
  let restartTimer = null;
  let attachTimer = null;
  let watchdogTimer = null;
  let restartAttempts = 0;
  let networkFailures = 0;
  let sawAnyResult = false;
  let starting = false;
  let noResultTimer = null;
  let onDeviceChecked = false;
  let onDeviceEnabled = false;

  // --- audio bridge: remote track -> AudioContext -> local track ------------
  let audioCtx = null;
  let bridgeSource = null;
  let bridgeDest = null;
  let bridgedTrack = null;
  let bridgedFromTrackId = null;
  let gestureCleanup = null;
  let analyser = null;
  let analyserBuffer = null;
  let levelTimer = null;
  // Rolling [{ t, rms }] of the remote leg's level, pruned to the guard window.
  let levelSamples = [];
  let leakStrikes = 0;
  let audioTrackVerdictSent = false;

  function stopLevelMeter() {
    if (levelTimer) {
      clearInterval(levelTimer);
      levelTimer = null;
    }
    levelSamples = [];
    analyser = null;
    analyserBuffer = null;
  }

  function sampleLevel() {
    if (!analyser || !analyserBuffer) return;
    analyser.getFloatTimeDomainData(analyserBuffer);
    let sum = 0;
    for (let i = 0; i < analyserBuffer.length; i += 1) {
      sum += analyserBuffer[i] * analyserBuffer[i];
    }
    const rms = Math.sqrt(sum / analyserBuffer.length);
    const now = Date.now();
    levelSamples.push({ t: now, rms });
    const cutoff = now - CUSTOMER_NATIVE_LEAK_WINDOW_MS;
    while (levelSamples.length && levelSamples[0].t < cutoff) levelSamples.shift();
  }

  /** Loudest remote-leg level seen inside the guard window. */
  function peakRecentLevel() {
    const cutoff = Date.now() - CUSTOMER_NATIVE_LEAK_WINDOW_MS;
    let peak = 0;
    for (let i = 0; i < levelSamples.length; i += 1) {
      if (levelSamples[i].t >= cutoff && levelSamples[i].rms > peak) {
        peak = levelSamples[i].rms;
      }
    }
    return peak;
  }

  function isLikelyMicLeak() {
    // No meter (bridge unavailable) means no evidence either way; don't accuse.
    if (!analyser || levelSamples.length === 0) return false;
    return peakRecentLevel() < CUSTOMER_NATIVE_SILENCE_RMS;
  }

  function removeGestureListeners() {
    if (gestureCleanup) gestureCleanup();
  }
  function ensureContextRunning() {
    if (!audioCtx) return;
    audioCtx.resume().catch(() => {});
    if (audioCtx.state === "running" || gestureCleanup) return;
    const resume = () => {
      if (audioCtx) audioCtx.resume().catch(() => {});
      removeGestureListeners();
    };
    const events = ["pointerdown", "keydown", "touchstart"];
    events.forEach((e) =>
      document.addEventListener(e, resume, { once: true, capture: true })
    );
    gestureCleanup = () => {
      events.forEach((e) =>
        document.removeEventListener(e, resume, { capture: true })
      );
      gestureCleanup = null;
    };
  }

  function teardownBridge() {
    removeGestureListeners();
    stopLevelMeter();
    if (bridgeSource) {
      try {
        bridgeSource.disconnect();
      } catch (e) {
        /* noop */
      }
    }
    if (bridgeDest) {
      try {
        bridgeDest.disconnect();
      } catch (e) {
        /* noop */
      }
    }
    if (audioCtx) {
      try {
        audioCtx.close();
      } catch (e) {
        /* noop */
      }
    }
    bridgeSource = null;
    bridgeDest = null;
    bridgedTrack = null;
    bridgedFromTrackId = null;
    audioCtx = null;
  }

  function ensureBridgedTrack(remoteTrack) {
    if (
      bridgedTrack &&
      bridgedTrack.readyState === "live" &&
      bridgedFromTrackId === remoteTrack.id
    ) {
      ensureContextRunning();
      return bridgedTrack;
    }
    // Remote track changed (new call / renegotiation) — rebuild.
    teardownBridge();
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      audioCtx = new AC();
      bridgeSource = audioCtx.createMediaStreamSource(
        new MediaStream([remoteTrack])
      );
      bridgeDest = audioCtx.createMediaStreamDestination();
      bridgeSource.connect(bridgeDest);
      // Level meter on the same source, feeding the contamination guard.
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      analyserBuffer = new Float32Array(analyser.fftSize);
      bridgeSource.connect(analyser);
      levelSamples = [];
      if (levelTimer) clearInterval(levelTimer);
      levelTimer = setInterval(sampleLevel, 150);
      bridgedTrack = bridgeDest.stream.getAudioTracks()[0] || null;
      bridgedFromTrackId = remoteTrack.id;
      ensureContextRunning();
      log("Bridge", {
        contextState: audioCtx.state,
        sampleRate: audioCtx.sampleRate,
        bridgedTrackId: bridgedTrack && bridgedTrack.id,
      });
      return bridgedTrack;
    } catch (e) {
      log("bridge-failed", e && e.message);
      teardownBridge();
      return null;
    }
  }
  // Bumped on every start so callbacks from a discarded instance can't mutate
  // state belonging to the current one.
  let sessionId = 0;

  function clearRestartTimer() {
    if (restartTimer) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }
  }
  function clearAttachTimer() {
    if (attachTimer) {
      clearTimeout(attachTimer);
      attachTimer = null;
    }
  }
  function clearWatchdog() {
    if (watchdogTimer) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
  }
  function clearNoResultTimer() {
    if (noResultTimer) {
      clearTimeout(noResultTimer);
      noResultTimer = null;
    }
  }
  function armNoResultTimer() {
    if (noResultTimer || sawAnyResult || !noResultTimeoutMs) return;
    noResultTimer = setTimeout(() => {
      noResultTimer = null;
      if (manualStop || sawAnyResult) return;
      log(
        "No Results",
        `nothing recognized in ${noResultTimeoutMs}ms — engine accepts the session but yields no audio`
      );
      onStatus("no-audio");
    }, noResultTimeoutMs);
  }
  function armWatchdog() {
    clearWatchdog();
    if (!silenceTimeoutMs) return;
    watchdogTimer = setTimeout(() => {
      watchdogTimer = null;
      if (manualStop || paused || !shouldRestart()) return;
      log("Watchdog Restart", `no result for ${silenceTimeoutMs}ms`);
      forceRestart();
    }, silenceTimeoutMs);
  }
  function scheduleRestart() {
    clearRestartTimer();
    const base = Math.min(
      restartDelayMs * Math.pow(2, restartAttempts),
      maxRestartDelayMs
    );
    // Jitter keeps this recognizer from restarting in lockstep with the agent's,
    // which would have both hitting the speech backend at the same instant.
    const delay = base + Math.floor(Math.random() * 400);
    restartAttempts += 1;
    restartTimer = setTimeout(() => {
      restartTimer = null;
      if (!manualStop && !paused && shouldRestart()) start();
    }, delay);
  }
  /**
   * True when this session should run on-device.
   *
   * The server backend is already occupied by the agent recognizer for the whole
   * call, and a second concurrent server session comes back as `network`.
   * On-device sidesteps that. Availability is per language, so an unavailable
   * pack quietly falls back to the server path.
   */
  async function resolveProcessLocally() {
    if (!preferOnDevice) return false;
    if (onDeviceChecked) return onDeviceEnabled;
    const Ctor = getCtor();
    if (!Ctor || typeof Ctor.available !== "function") {
      onDeviceChecked = true;
      onDeviceEnabled = false;
      log("On-device Unsupported", "SpeechRecognition.available() missing");
      return false;
    }
    try {
      const state = await Ctor.available({
        langs: [lang],
        processLocally: true,
        quality: onDeviceQuality,
      });
      log("On-device Availability", `${lang} -> ${state}`);
      onDeviceChecked = true;
      if (state === "available") {
        onDeviceEnabled = true;
        return true;
      }
      if (state === "downloadable" || state === "downloading") {
        // Kick the download off but don't block this call on it; when it lands,
        // clear the cache so the next session upgrades to on-device.
        if (typeof Ctor.install === "function") {
          Ctor.install({ langs: [lang], processLocally: true })
            .then((ok) => {
              log("On-device Install", ok ? "installed" : "failed");
              if (ok) onDeviceChecked = false;
            })
            .catch((e) => log("On-device Install failed", e && e.message));
        }
      }
      onDeviceEnabled = false;
      return false;
    } catch (e) {
      log("On-device check failed", e && e.message);
      onDeviceChecked = true;
      onDeviceEnabled = false;
      return false;
    }
  }

  function detach(r) {
    if (!r) return;
    r.onresult = null;
    r.onend = null;
    r.onerror = null;
  }
  function releaseTrack() {
    if (!clonedTrack) return;
    try {
      clonedTrack.stop();
    } catch (e) {
      /* noop */
    }
    clonedTrack = null;
  }
  function teardownRecognition() {
    const old = recognition;
    detach(old);
    recognition = null;
    running = false;
    if (old) {
      try {
        if (typeof old.abort === "function") old.abort();
        else old.stop();
      } catch (e) {
        /* noop */
      }
    }
    releaseTrack();
  }
  function forceRestart() {
    teardownRecognition();
    scheduleRestart();
  }

  function handleResult(event) {
    if (paused) return;

    // Contamination guard, before anything is published: text produced while the
    // remote leg was silent came from the microphone, not the customer.
    if (isLikelyMicLeak()) {
      leakStrikes += 1;
      log(
        "Suspicious Result",
        `remote leg silent (peak ${peakRecentLevel().toFixed(
          4
        )}) — strike ${leakStrikes}/${CUSTOMER_NATIVE_LEAK_STRIKES}`
      );
      if (leakStrikes >= CUSTOMER_NATIVE_LEAK_STRIKES) {
        if (!audioTrackVerdictSent) {
          audioTrackVerdictSent = true;
          log(
            "audioTrack Ignored",
            "this browser transcribes the microphone instead of the given track — disabling customer transcription"
          );
          onAudioTrackUnsupported();
        }
        manualStop = true;
        clearRestartTimer();
        clearAttachTimer();
        clearWatchdog();
        clearNoResultTimer();
        sessionId += 1;
        teardownRecognition();
        teardownBridge();
        onStatus("unsupported");
      }
      // Never publish a suspected leak, strike limit reached or not.
      return;
    }

    restartAttempts = 0;
    networkFailures = 0;
    sawAnyResult = true;
    clearNoResultTimer();
    armWatchdog();
    let interim = "";
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const res = event.results[i];
      const text = (res && res[0] && res[0].transcript) || "";
      if (res.isFinal) {
        const finalText = text.trim();
        if (finalText) {
          log("Final", finalText);
          onFinal(finalText);
        }
      } else {
        interim += text;
      }
    }
    const trimmedInterim = interim.trim();
    if (trimmedInterim) {
      log("Interim", trimmedInterim);
      onInterim(trimmedInterim);
    }
  }

  function handleEnd() {
    running = false;
    clearWatchdog();
    // The track clone is tied to the finished session; the next start clones again.
    releaseTrack();
    if (manualStop) return;
    if (!paused && shouldRestart()) {
      log("Restarting");
      scheduleRestart();
      return;
    }
    log("Stopped");
    onStatus(paused ? "paused" : "stopped");
  }

  function handleError(event) {
    const code = (event && event.error) || "unknown";
    log("Error", code);
    // Routine on a live call: the customer is often silent, and a restart
    // aborts the previous instance by design.
    if (code !== "no-speech" && code !== "aborted") onError(code);
    if (code === "no-speech") {
      // Silence is the normal state while the agent talks, so it must not feed
      // the backoff — a grown delay would clip the start of the customer's next
      // sentence. Keep cycling at the base interval instead.
      restartAttempts = 0;
    }
    if (code === "network") {
      networkFailures += 1;
      // Repeated network failures without a single result means the recognizer
      // never streamed successfully — say so instead of leaving the pane on
      // "Listening…" indefinitely.
      if (!sawAnyResult && networkFailures >= networkFailureLimit) {
        log("Network Failing", `${networkFailures} attempts, no results`);
        onStatus("error");
      }
    }
    if (code === "not-allowed" || code === "audio-capture") {
      manualStop = true;
    }
  }

  function resolveTrack() {
    const stream = getStream();
    const track =
      stream && typeof stream.getAudioTracks === "function"
        ? stream.getAudioTracks()[0]
        : null;
    if (!track || track.readyState !== "live") return null;
    // A received WebRTC track reports muted until the first RTP packets arrive.
    // Starting against it throws InvalidStateError, so treat muted as not-ready
    // and let the caller retry.
    if (track.muted) return null;
    return track;
  }

  async function start() {
    const Ctor = getCtor();
    if (!Ctor) {
      log("Browser Unsupported");
      onStatus("unsupported");
      return false;
    }
    // start() awaits the on-device check, so without this a restart timer and a
    // resume could both be inside that window and create two live recognizers.
    if (starting) return false;
    // Without audioTrack support start(track) would listen to the microphone and
    // attribute the agent's speech to the customer. Refuse instead.
    if (!isAudioTrackSupported()) {
      log("audioTrack Unsupported");
      onStatus("unsupported");
      return false;
    }
    if (running) return false;
    if (paused) return false;

    let processLocally = false;
    starting = true;
    try {
      processLocally = await resolveProcessLocally();
    } finally {
      starting = false;
    }
    // State can have moved while the availability check was in flight.
    if (manualStop || paused || running || !shouldRestart()) return false;

    const track = resolveTrack();
    if (!track) {
      // Remote track not attached yet, still muted, or already ended — poll.
      clearAttachTimer();
      attachTimer = setTimeout(() => {
        attachTimer = null;
        if (!manualStop && !paused && shouldRestart()) start();
      }, attachRetryMs);
      onStatus("waiting");
      return false;
    }

    manualStop = false;
    detach(recognition);
    releaseTrack();
    sessionId += 1;
    const mySession = sessionId;
    const stale = () => mySession !== sessionId;

    if (cloneTrack) {
      try {
        clonedTrack = track.clone();
      } catch (e) {
        log("clone-failed", e && e.message);
        clonedTrack = null;
      }
    }

    let recognitionTrack = clonedTrack || track;
    let bridged = false;
    if (useAudioBridge) {
      const viaBridge = ensureBridgedTrack(track);
      if (viaBridge) {
        recognitionTrack = viaBridge;
        bridged = true;
      } else {
        log("Bridge unavailable", "falling back to the raw remote track");
      }
    }

    // Track state is the first thing to check when no results arrive: a muted or
    // non-live track means the recognizer is being fed silence.
    log("Track", {
      id: recognitionTrack.id,
      readyState: recognitionTrack.readyState,
      muted: recognitionTrack.muted,
      enabled: recognitionTrack.enabled,
      cloned: !!clonedTrack,
      bridged,
    });

    recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    if (processLocally) {
      // Only set when the pack is confirmed available: setting it without one
      // fails start() with language-not-supported.
      recognition.processLocally = true;
    }
    recognition.onresult = (e) => {
      if (stale()) return;
      handleResult(e);
    };
    recognition.onend = () => {
      if (stale()) return;
      handleEnd();
    };
    recognition.onerror = (e) => {
      if (stale()) return;
      handleError(e);
    };

    try {
      recognition.start(recognitionTrack);
      running = true;
      armNoResultTimer();
      log(
        "Started",
        `lang=${lang} mode=${processLocally ? "on-device" : "server"} source=${
          bridged ? "bridge" : "remote-track"
        }`
      );
      onStatus("running");
      armWatchdog();
      return true;
    } catch (e) {
      running = false;
      detach(recognition);
      recognition = null;
      releaseTrack();
      const name = (e && e.name) || "unknown";
      log("Start Failed", name);
      if (!manualStop && !paused && shouldRestart()) {
        scheduleRestart();
        return false;
      }
      onError("start-failed");
      onStatus("stopped");
      return false;
    }
  }

  function stop() {
    manualStop = true;
    clearRestartTimer();
    clearAttachTimer();
    clearWatchdog();
    clearNoResultTimer();
    sessionId += 1; // invalidate in-flight callbacks
    teardownRecognition();
    // The bridge survives recognition restarts, so it is only closed here.
    teardownBridge();
    log("Stopped");
    onStatus("stopped");
  }

  // Hold plays music down the remote leg, which is not the customer speaking.
  function setPaused(next) {
    const value = Boolean(next);
    if (value === paused) return;
    paused = value;
    if (paused) {
      clearRestartTimer();
      clearAttachTimer();
      clearWatchdog();
      sessionId += 1;
      teardownRecognition();
      log("Paused");
      onStatus("paused");
    } else {
      log("Resumed");
      if (!manualStop) start();
    }
  }

  return { start, stop, setPaused, isRunning: () => running };
}
